/**
 * TEMU 发品表单提交前校验（对齐 publishOrEdit 常用必填与格式）。
 * 图片只需已上传（有可访问地址）；主图/细节/素材上传时已走 Temu 平台转图。
 * @returns {{ ok: true } | { ok: false, message: string, section?: string }}
 */

import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import {
  normalizeTemuOuterPackageShape,
  normalizeTemuOuterPackageType,
} from '@/temu-platform/utils/temuOuterPackage';
import {
  attributeDisplayLabel,
  isBlankTemuAttributeValueId,
  resolveSkcColorAgainstTemplate,
} from '@/temu-platform/utils/temuAttributeTemplate';
import { EMPTY_SKU_PACKAGE_CAPS } from '@/temu-platform/utils/temuPublishFillInStandard';
import { TEMU_MODE_FULL, TEMU_MODE_LOCAL, TEMU_MODE_SEMI, temuShopCapabilities } from '@/temu-platform/utils/temuShopCapabilities';
import {
  normalizeTemuOriginRegion,
  resolveTemuRegion2Id,
} from '@/temu-platform/utils/temuProductOrigin';
import {
  getTemuShipmentLimitOptions,
  isTemuCustomProductMode,
  isValidTemuShipmentLimitSecond,
} from '@/temu-platform/utils/temuShipmentLimitOptions';
import { buildTemuManufacturingLocation, validateTemuCustomizedTechnology } from '@/temu-platform/utils/temuCustomizedTechnology';
import { validateSupplierSkusLocal } from '@/temu-platform/utils/temuPublishPreflight';
import { validateSizeChartForm } from '@/temu-platform/utils/temuSizeChart';
import {
  collectEffectivelyRequiredAttrs,
  collectMissingParentAttrIssues,
  isEditorAttrFilled,
} from '@/temu-platform/utils/temuAttrVisibility';
import {
  hasTemuVehicleLibraryData,
  isTemuVehicleLibraryDataRequired,
} from '@/temu-platform/utils/temuVehicleLibraryRequired';
import {
  resolveTemuCat1Id,
  validateTemuProductNameLength,
} from '@/temu-platform/utils/temuProductNameLength';
import {
  isIllegalTemuTryOnSizeName,
  normalizeTemuTryOnSizeName,
  resolveGoodsModelTryOnSize,
  buildTemuModelTryOnSizeOptions,
} from '@/temu-platform/utils/temuOfficialSizeChart';
import { validateDimsOrder } from '@/temu-platform/utils/temuPackageBatch';
import {
  collectTemuSkcPreviewImgUrls,
  TEMU_CLOTHING_SKC_CAROUSEL_MIN,
  TEMU_MAX_DETAIL_IMAGES,
} from '@/temu-platform/utils/temuProductImage';

/** 发品提交：必须已有 Temu 平台图 URL */
function imageUrlOf(image) {
  if (!image) return '';
  return String(image.temuUrl || '').trim();
}

function isPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

function isNonNegativeInteger(value) {
  const text = String(value ?? '').trim();
  if (!/^\d+$/.test(text)) return false;
  return Number(text) >= 0;
}

function fail(message, section, field = 'form') {
  return { ok: false, message: fixedUi(message), section, field };
}

/** 本土 V3：不校验跨境外包装/素材图/产地证明；聚焦货号、主图、SKU 价与图。 */
function validateTemuLocalPublishForm(form, ctx, failFn) {
  const title = String(form.title || form.productName || '').trim();
  if (!title) return failFn('请填写商品名称', 'basic', 'title');
  if (title.length < 2) return failFn('商品名称至少 2 个字符', 'basic', 'title');

  const supplierCode = String(
    form.supplierCode || form.extCode || form.externalGoodsId || form.outGoodsSn || '',
  ).trim();
  if (!supplierCode) {
    return failFn('请填写货号（本土发品 externalGoodsId 必填）', 'basic', 'supplierCode');
  }

  const skcList = Array.isArray(form.skcList) ? form.skcList : [];
  const hasAnyImage = skcList.some((skc) => {
    const main = skc?.mainImage;
    const u = main
      ? String(main.temuUrl || main.publicUrl || main.ipeUrl || main.url || '').trim()
      : '';
    if (u) return true;
    return (Array.isArray(skc?.detailImages) ? skc.detailImages : []).some((img) => (
      String(img?.temuUrl || img?.publicUrl || img?.ipeUrl || img?.url || '').trim()
    ));
  }) || (Array.isArray(form.carouselImages) ? form.carouselImages : []).some((img) => (
    String(typeof img === 'string' ? img : (img?.temuUrl || img?.publicUrl || img?.url || '')).trim()
  ));
  if (!hasAnyImage) {
    return failFn('请至少上传 1 张商品主图', 'images', 'mainImage');
  }

  let skuCount = 0;
  for (const skc of skcList.length ? skcList : [{}]) {
    const rows = Array.isArray(skc?.skuRows) && skc.skuRows.length ? skc.skuRows : [{}];
    for (const sku of rows) {
      skuCount += 1;
      const price = Number(
        sku?.supplyPrice ?? sku?.price ?? sku?.supplierPriceYuan ?? form.supplyPrice ?? form.defaultPriceYuan,
      );
      if (!Number.isFinite(price) || price <= 0) {
        return failFn('请为每个 SKU 填写大于 0 的售价/供货价', 'sku', 'supplyPrice');
      }
    }
  }
  if (skuCount < 1) {
    return failFn('请至少配置 1 个 SKU', 'sku', 'form');
  }

  if (Number(form.isCustomProduct ?? form.personalizationSwitch) === 1) {
    const tech = validateTemuCustomizedTechnology(form);
    if (!tech.ok) return failFn(tech.message, 'sale', tech.field || 'customizedTechnology');
  }

  return { ok: true };
}

/**
 * @param {object} form
 * @param {object} ctx
 * @param {boolean} [ctx.isEdit]
 * @param {string|number} [ctx.shopId]
 * @param {object|null} [ctx.attrTpl]
 * @param {object|null} [ctx.colorAttr]
 * @param {object|null} [ctx.sizeAttr]
 * @param {Array} [ctx.sizeMeasureAttrs]
 * @param {boolean} [ctx.sizeChartRequired] 官方 sizecharts.class.get 判定
 * @param {boolean} [ctx.modelRequired] 叶子类目是否要求填写模特（平台公告清单）
 * @param {boolean} [ctx.netWeightRequired] 叶子类目是否要求填写货品净重（平台公告清单）
 * @param {boolean} [ctx.semiSkuNetContentRequired] 半托管 + 清单类目：SKU 分类与净含量必填
 * @param {Array} [ctx.warehouseOptions]
 * @param {{ quantityInfo?: { show?: boolean, required?: boolean }, packageType?: { show?: boolean, required?: boolean } }} [ctx.skuPkgCaps]
 * @param {string} [ctx.shopType]
 */
export function validateTemuPublishForm(form = {}, ctx = {}) {
  const {
    isEdit = false,
    shopId,
    shopType = '',
    attrTpl = null,
    colorAttr = null,
    sizeAttr = null,
    sizeMeasureAttrs = [],
    sizeChartRequired = false,
    modelRequired = false,
    netWeightRequired = false,
    semiSkuNetContentRequired = false,
    warehouseOptions = [],
    skuPkgCaps = EMPTY_SKU_PACKAGE_CAPS,
  } = ctx;
  const quantityCap = {
    show: skuPkgCaps?.quantityInfo?.show === true,
    required: skuPkgCaps?.quantityInfo?.required === true,
  };
  const packageCap = {
    show: skuPkgCaps?.packageType?.show === true,
    required: skuPkgCaps?.packageType?.required === true,
  };

  if (!shopId) return fail('请先选择店铺', 'basic', 'form');

  const shopCapsEarly = temuShopCapabilities(shopType || form.shopType);
  if (!shopCapsEarly.mode) {
    return fail('当前店铺类型未知，无法按全托/半托规则发品，请重新授权或同步店铺资料', 'basic', 'form');
  }

  const isLocalManaged = shopCapsEarly.mode === TEMU_MODE_LOCAL;
  if (isLocalManaged) {
    return validateTemuLocalPublishForm(form, ctx, fail);
  }

  const title = String(form.title || form.productName || '').trim();
  if (!title) return fail('请填写商品名称', 'basic', 'title');
  if (title.length < 2) return fail('商品名称至少 2 个字符', 'basic', 'title');
  const titleLenCheck = validateTemuProductNameLength(title, resolveTemuCat1Id(form));
  if (!titleLenCheck.ok) {
    return fail(titleLenCheck.message, 'basic', 'title');
  }

  // 英文名称由商品名称自动翻译，提交前会补齐；此处若仍空则提示等待翻译
  const titleEn = String(form.titleEn || form.englishTitle || '').trim();
  if (!titleEn) {
    return fail('英文名称尚未生成，请稍候自动翻译完成后再提交', 'basic', 'titleEn');
  }
  const titleEnLenCheck = validateTemuProductNameLength(titleEn, resolveTemuCat1Id(form));
  if (!titleEnLenCheck.ok) {
    return fail(
      titleEnLenCheck.message.replace('商品名称', '英文名称'),
      'basic',
      'titleEn',
    );
  }
  if (/[\u3400-\u9fff]/.test(titleEn)) {
    return fail('英文名称不能包含中文，请重新触发翻译', 'basic', 'titleEn');
  }
  // 半托管英文标题过短会被平台拒绝（2000177）
  if (shopCapsEarly.mode === TEMU_MODE_SEMI && titleEn.replace(/\s+/g, '').length < 3) {
    return fail('半托管英文名称过短，请完善后再提交', 'basic', 'titleEn');
  }

  if (!form.categoryId) return fail('请选择商品类目', 'basic', 'categoryId');
  if (!form.productTypeId) return fail('当前类目缺少商品类型，请重新选择末级类目', 'basic', 'categoryId');

  const originRegion = normalizeTemuOriginRegion(form.originRegion || form.originCountry || form.productOrigin?.region1ShortName || form.productOrigin?.countryShortName);
  if (!originRegion) {
    return fail('请选择产地（二字码）', 'basic', 'originRegion');
  }
  if (originRegion === 'CN' && !(resolveTemuRegion2Id(
    form.originRegion2Id || form.region2Id || form.productOrigin?.region2Id,
  ) > 0)) {
    return fail(fixedUi('产地为中国时请选择省份'), 'basic', 'originRegion2Id');
  }

  const isFullManaged = shopCapsEarly.mode === TEMU_MODE_FULL;
  if (isFullManaged && !buildTemuManufacturingLocation(form)) {
    return fail('全托管发品请选择制造地点', 'basic', 'customManufacturingLocations');
  }
  if (isFullManaged && originRegion && originRegion !== 'CN') {
    const certs = Array.isArray(form.originCertFiles) ? form.originCertFiles : [];
    const ok = certs.some((f) => String(f?.fileUrl || f?.url || '').trim());
    if (!ok) {
      return fail('全托管且产地非中国大陆时请上传产地证明文件', 'basic', 'originCertFiles');
    }
  }

  const shapeRaw = form.packageShape;
  const typeRaw = form.packageTypeOuter ?? form.outerPackageType;
  if (shapeRaw === '' || shapeRaw == null) {
    return fail('请选择外包装形状', 'size', 'packageShape');
  }
  if (normalizeTemuOuterPackageShape(shapeRaw) == null) {
    return fail('外包装形状不合法，请选择长方体 / 圆柱体 / 其他不规则形状', 'size', 'packageShape');
  }
  if (typeRaw === '' || typeRaw == null) {
    return fail('请选择外包装类型', 'size', 'packageTypeOuter');
  }
  if (normalizeTemuOuterPackageType(typeRaw) == null) {
    return fail('外包装类型不合法，请选择硬包装 / 软包装+硬物 / 软包装+软物', 'size', 'packageTypeOuter');
  }
  const outerPkgImages = Array.isArray(form.outerPackageImages) ? form.outerPackageImages : [];
  const outerPkgUrls = Array.isArray(form.outerPackageImageUrls) ? form.outerPackageImageUrls : [];
  const outerPkgReqs = Array.isArray(form.productOuterPackageImageReqs)
    ? form.productOuterPackageImageReqs
    : [];
  const countOuterPkgTemu = () => {
    let n = 0;
    outerPkgImages.forEach((img) => {
      if (!img) return;
      if (typeof img === 'string') {
        if (/cdnfe|pinduoduo|temu\.com|kwcdn/i.test(String(img))) n += 1;
        return;
      }
      if (String(img.temuUrl || '').trim()) n += 1;
    });
    outerPkgUrls.forEach((u) => { if (String(u || '').trim()) n += 1; });
    outerPkgReqs.forEach((r) => {
      if (String(r?.imageUrl || r?.temuUrl || '').trim()) n += 1;
    });
    return n;
  };
  const outerPkgCount = countOuterPkgTemu();
  if (outerPkgCount < 1) {
    return fail('请上传外包装图片（1–6 张，须含正拍/侧拍/俯拍，且完成 Temu 转图）', 'size', 'outerPackageImages');
  }
  if (outerPkgCount < 3) {
    return fail('外包装图片建议至少 3 张（正拍图、侧拍图、俯拍图）', 'size', 'outerPackageImages');
  }
  const hasProductOpenOuter = [...outerPkgImages, ...outerPkgUrls, ...outerPkgReqs].some((item) => {
    const u = typeof item === 'string'
      ? item
      : String(item?.temuUrl || item?.imageUrl || item?.url || '');
    return /\/product\/open\//i.test(u) || (/kwcdn\.com\/product\//i.test(u) && !/private-tag/i.test(u));
  });
  if (hasProductOpenOuter) {
    return fail(
      '外包装图须用 imageBizType=1 重新上传（勿复用货品主图 CDN）。请删除后重新上传外包装图片',
      'size',
      'outerPackageImages',
    );
  }

  if (ctx.needGuideFile) {
    const guides = Array.isArray(form.guideFiles) ? form.guideFiles : [];
    const okGuide = guides.some((f) => Number(f?.pdfMaterialId || f?.pdfId) > 0);
    if (!okGuide) {
      return fail('当前类目要求上传电子说明书（PDF）', 'spec', 'guideFiles');
    }
  }

  if (!isEdit && !(form.selectedSites || []).length) {
    const caps = temuShopCapabilities(shopType || form.shopType);
    if (caps.usesPublishSiteList || caps.mode === TEMU_MODE_SEMI) {
      return fail('请选择上架站点', 'sales', 'selectedSites');
    }
  }

  if (!String(form.currency || '').trim()) {
    const shopCapsForCurrency = temuShopCapabilities(shopType || form.shopType);
    const selfOperated = shopCapsForCurrency.usesPriceInfo;
    return fail(
      selfOperated
        ? '缺少售价币种：请等待店铺站点列表币种加载完成'
        : '缺少供货价币种：请等待 Temu 发品规范币种加载完成',
      'sales',
      'currency',
    );
  }

  const shopCaps = temuShopCapabilities(shopType || form.shopType);
  const isSemi = shopCaps.mode === TEMU_MODE_SEMI;
  const skipSiteWarehouseFreight = Boolean(shopCaps.skipSiteWarehouseFreight);
  if (!skipSiteWarehouseFreight) {
    if (isSemi && !String(form.freightTemplateId || '').trim()) {
      return fail('半托管发品请选择运费模板', 'sales', 'freightTemplateId');
    }
    if (isSemi && !String(form.warehouseId || '').trim()) {
      return fail('半托管发品请选择库存仓库', 'sales', 'warehouseId');
    }
    if (isSemi) {
      const shipmentOpts = getTemuShipmentLimitOptions({
        siteIds: form.selectedSites || form.siteIds,
        customProduct: form.isCustomProduct ?? form.personalizationSwitch,
      });
      if (!isValidTemuShipmentLimitSecond(form.shipmentLimitSecond, shipmentOpts)) {
        const modeLabel = isTemuCustomProductMode(form.isCustomProduct ?? form.personalizationSwitch)
          ? '定制品'
          : '普通品';
        return fail(
          fixedUiTpl('承诺发货时效不符合当前站点（{0}）可选值，请重新选择', modeLabel),
          'sales',
          'shipmentLimitSecond',
        );
      }
    }

    // 多仓：stock_info_list.supplier_warehouse_id 必填，需明确库存归属仓库
    if (Array.isArray(warehouseOptions) && warehouseOptions.length > 1 && !String(form.warehouseId || '').trim()) {
      return fail('当前店铺为多仓，请选择库存所在仓库', 'sales', 'warehouseId');
    }
  }

  const customTechCheck = validateTemuCustomizedTechnology(form);
  if (!customTechCheck.ok) {
    return fail(customTechCheck.message, 'sales', customTechCheck.field || 'customFirstType');
  }

  const skcLabel = attributeDisplayLabel(colorAttr) || fixedUi("主规格");
  const skuSpecLabel = attributeDisplayLabel(sizeAttr) || fixedUi('其他规格');

  const skcList = Array.isArray(form.skcList) ? form.skcList : [];
  const coloredSkcs = skcList.filter((skc) => (
    skc?.colorValueId
    || String(skc?.colorExtraValue || '').trim()
    || String(skc?.colorLabel || '').trim()
  ));
  if (!coloredSkcs.length) {
    return fail(colorAttr ? fixedUiTpl('请选择或自定义{0}', skcLabel) : fixedUiTpl('请添加{0}', skcLabel), 'spec', 'skcList');
  }

  if (sizeAttr?.values?.length && !(form.selectedSizeIds || []).length) {
    return fail(fixedUiTpl('请选择{0}', skuSpecLabel), 'spec', 'skcList');
  }

  for (const skc of coloredSkcs) {
    const resolved = resolveSkcColorAgainstTemplate(skc, colorAttr);
    const colorName = resolved.colorLabel || resolved.colorExtraValue || skc.colorLabel || skc.colorExtraValue || skcLabel;
    const colorAttrId = String(resolved.colorAttributeId || colorAttr?.attributeId || '').trim();
    let skcPriceAnchor = '';
    if (colorAttr && !colorAttrId) {
      return fail(fixedUiTpl('「{0}」缺少{1}属性，请刷新类目属性模板后重试', colorName, skcLabel), 'spec', 'skcList');
    }
    if (colorAttr && isBlankTemuAttributeValueId(resolved.colorValueId)) {
      const customLabel = String(resolved.colorExtraValue || resolved.colorLabel || '').trim();
      if (!customLabel) {
        return fail(
          fixedUiTpl('「{0}」的{1}未匹配到平台属性值，请在模板中重新选择颜色', colorName, skcLabel),
          'spec',
          'skcList',
        );
      }
    }
    if (!imageUrlOf(skc.mainImage)) {
      return fail(fixedUiTpl('请为「{0}」上传主图（须完成 Temu 平台转图）', colorName), 'spec', 'skcList');
    }
    const details = Array.isArray(skc.detailImages) ? skc.detailImages : [];
    if (details.length > TEMU_MAX_DETAIL_IMAGES) {
      return fail(fixedUiTpl('「{0}」细节图最多 {1} 张', colorName, TEMU_MAX_DETAIL_IMAGES), 'spec', 'skcList');
    }
    for (let i = 0; i < details.length; i += 1) {
      if (!imageUrlOf(details[i])) {
        return fail(fixedUiTpl('「{0}」第 {1} 张细节图无效，请重新上传', colorName, i + 1), 'spec', 'skcList');
      }
    }
    // 服饰类目：平台校验每个 SKC 的 previewImgUrls（主图+细节图）≥5
    if (colorAttr) {
      const previewCount = collectTemuSkcPreviewImgUrls(skc, imageUrlOf).length;
      if (previewCount < TEMU_CLOTHING_SKC_CAROUSEL_MIN) {
        return fail(
          fixedUiTpl('「{0}」商品图（主图+细节图）至少 {1} 张，当前 {2} 张', colorName, TEMU_CLOTHING_SKC_CAROUSEL_MIN, previewCount),
          'spec',
          'skcList',
        );
      }
    }
    const skcExtCode = String(skc.supplierCode || '').trim();
    if (!skcExtCode) {
      return fail(fixedUiTpl('请填写「{0}」货号', colorName), 'spec', 'skcList');
    }
    if (!/^[A-Za-z0-9_\u4e00-\u9fff+-]{1,100}$/.test(skcExtCode)) {
      return fail(
        fixedUiTpl('「{0}」货号仅支持字母、数字、中文、下划线、短横线、加号，最长 100 位', colorName),
        'spec',
        'skcList',
      );
    }

    const skuRows = Array.isArray(skc.skuRows) ? skc.skuRows : [];
    if (!skuRows.length) {
      return fail(fixedUiTpl('「{0}」缺少 SKU 供应信息，请选择{1}或检查规格', colorName, skuSpecLabel), 'spec', 'supply');
    }

    for (const sku of skuRows) {
      const sizeLabel = sku.sizeValueId || 'SKU';
      const price = sku.price ?? form.supplyPrice;
      if (price === '' || price == null) {
        return fail(fixedUiTpl('请填写「{0} / {1}」供货价', colorName, sizeLabel), 'spec', 'supply');
      }
      if (!isPositiveNumber(price)) {
        return fail(fixedUiTpl('「{0} / {1}」供货价须为大于 0 的数字', colorName, sizeLabel), 'spec', 'supply');
      }

      const stock = sku.stock === '' || sku.stock == null ? form.stockQty : sku.stock;
      if (stock === '' || stock == null) {
        return fail(fixedUiTpl('请填写「{0} / {1}」库存', colorName, sizeLabel), 'spec', 'supply');
      }
      if (!isNonNegativeInteger(stock)) {
        return fail(fixedUiTpl('「{0} / {1}」库存须为 ≥0 的整数', colorName, sizeLabel), 'spec', 'supply');
      }

      const weight = sku.weight ?? form.weightGrams;
      if (weight === '' || weight == null) {
        return fail(fixedUiTpl('请填写「{0} / {1}」包装重量（单位 g）', colorName, sizeLabel), 'size', 'supply');
      }
      if (!isPositiveNumber(weight)) {
        return fail(fixedUiTpl('「{0} / {1}」包装重量须为大于 0 的数字（单位 g）', colorName, sizeLabel), 'size', 'supply');
      }

      // 部分叶子类目货品净重必填 → productSkuNetWeightReq（页面 g，提交转 mg）
      const netWeight = sku.netWeight ?? form.netWeightGrams;
      if (netWeightRequired) {
        if (netWeight === '' || netWeight == null) {
          return fail(fixedUiTpl('当前类目要求填写「{0} / {1}」货品净重（单位 g）', colorName, sizeLabel), 'size', 'supply');
        }
        if (!isPositiveNumber(netWeight)) {
          return fail(fixedUiTpl('「{0} / {1}」货品净重须为大于 0 的数字（单位 g）', colorName, sizeLabel), 'size', 'supply');
        }
      } else if (netWeight !== '' && netWeight != null && !isPositiveNumber(netWeight)) {
        return fail(fixedUiTpl('「{0} / {1}」货品净重须为大于 0 的数字（单位 g）', colorName, sizeLabel), 'size', 'supply');
      }

      const length = sku.length ?? form.pkgLength;
      const width = sku.width ?? form.pkgWidth;
      const height = sku.height ?? form.pkgHeight;
      if (length === '' || length == null || width === '' || width == null || height === '' || height == null) {
        return fail(fixedUiTpl('请填写「{0} / {1}」包装体积（最长边/次长边/最短边，单位 cm）', colorName, sizeLabel), 'size', 'supply');
      }
      if (!isPositiveNumber(length) || !isPositiveNumber(width) || !isPositiveNumber(height)) {
        return fail(fixedUiTpl('「{0} / {1}」包装体积须为大于 0 的数字（单位 cm）', colorName, sizeLabel), 'size', 'supply');
      }
      const dimsOrderMsg = validateDimsOrder(length, width, height);
      if (dimsOrderMsg) {
        return fail(`「${colorName} / ${sizeLabel}」${dimsOrderMsg}`, 'size', 'supply');
      }

      // 平台：同一 SKC 下各 SKU 申报价须一致
      const priceKey = String(sku.supplyPrice ?? sku.price ?? form.supplyPrice ?? '');
      if (!skcPriceAnchor) {
        skcPriceAnchor = priceKey;
      } else if (priceKey !== skcPriceAnchor) {
        return fail(fixedUiTpl('「{0}」同一颜色下各尺码申报价须一致', colorName), 'spec', 'supply');
      }

      const isSensitive = Number(sku.isSensitive ?? form.isSensitive ?? 0) === 1 ? 1 : 0;
      if (isSensitive) {
        const list = Array.isArray(sku.sensitiveList) ? sku.sensitiveList.filter((x) => Number(x) > 0) : [];
        if (!list.length) {
          return fail(fixedUiTpl('「{0} / {1}」已选敏感，请至少选择一种敏感类型', colorName, sizeLabel), 'size', 'supply');
        }
      }

      const skuClass = Number(sku.skuClassification || 1);
      if (![1, 2, 3].includes(skuClass)) {
        return fail(
          semiSkuNetContentRequired
            ? fixedUiTpl('当前类目要求填写「{0} / {1}」SKU 分类', colorName, sizeLabel)
            : fixedUiTpl('「{0} / {1}」请选择 SKU 分类', colorName, sizeLabel),
          'size',
          'supply',
        );
      }
      // 半托管清单类目：计量净含量 productSkuNetContentReq（勿用货品净重 netWeight 顶替）
      if (semiSkuNetContentRequired) {
        const netContentRaw = String(sku.netContentNumber ?? sku.netContent ?? '').trim();
        if (!netContentRaw) {
          return fail(fixedUiTpl('当前类目要求填写「{0} / {1}」净含量（计量含量，不是货品净重）', colorName, sizeLabel), 'size', 'supply');
        }
        const netContentNum = Number(netContentRaw);
        if (!Number.isFinite(netContentNum) || netContentNum <= 0 || !Number.isInteger(netContentNum)) {
          return fail(fixedUiTpl('「{0} / {1}」净含量须为正整数', colorName, sizeLabel), 'size', 'supply');
        }
        const netUnit = Number(sku.netContentUnitCode || 0);
        if (![1, 2, 3, 4, 5, 6, 7, 8].includes(netUnit)) {
          return fail(fixedUiTpl('「{0} / {1}」请选择净含量单位', colorName, sizeLabel), 'size', 'supply');
        }
      } else {
        const netContentRaw = String(sku.netContentNumber ?? sku.netContent ?? '').trim();
        if (netContentRaw) {
          const netContentNum = Number(netContentRaw);
          if (!Number.isFinite(netContentNum) || netContentNum <= 0 || !Number.isInteger(netContentNum)) {
            return fail(fixedUiTpl('「{0} / {1}」净含量须为正整数', colorName, sizeLabel), 'size', 'supply');
          }
          const netUnit = Number(sku.netContentUnitCode || 0);
          if (netUnit && ![1, 2, 3, 4, 5, 6, 7, 8].includes(netUnit)) {
            return fail(fixedUiTpl('「{0} / {1}」净含量单位无效', colorName, sizeLabel), 'size', 'supply');
          }
        }
      }
      const piecesRaw = String(sku.numberOfPieces ?? sku.quantity ?? '').trim();
      if (!piecesRaw) {
        return fail(fixedUiTpl('请填写「{0} / {1}」单品数量', colorName, sizeLabel), 'size', 'supply');
      }
      const pieces = Number(piecesRaw);
      if (!Number.isInteger(pieces) || pieces <= 0) {
        return fail(fixedUiTpl('「{0} / {1}」单品数量须为正整数', colorName, sizeLabel), 'size', 'supply');
      }
      const pieceUnit = Number(sku.pieceUnitCode || sku.quantityUnit || 1);
      if (![1, 2, 3].includes(pieceUnit)) {
        return fail(fixedUiTpl('「{0} / {1}」请选择单件单位（件/双/包）', colorName, sizeLabel), 'size', 'supply');
      }
      if (skuClass === 2 || skuClass === 3) {
        const packed = sku.individuallyPacked;
        if (packed !== 0 && packed !== 1 && packed !== '0' && packed !== '1') {
          return fail(fixedUiTpl('「{0} / {1}」同款多件/混合套装须选择是否独立包装', colorName, sizeLabel), 'size', 'supply');
        }
      }

      if (skuClass === 3) {
        const accessories = Array.isArray(sku.accessories) ? sku.accessories : [];
        const validAcc = accessories.filter((row) => Number(row?.vid) > 0 && Number(row?.num) > 0);
        if (!validAcc.length) {
          return fail(fixedUiTpl('「{0} / {1}」混合套装须填写包装清单', colorName, sizeLabel), 'size', 'supply');
        }
        const sum = validAcc.reduce((s, row) => s + Math.trunc(Number(row.num)), 0);
        if (sum !== pieces) {
          return fail(
            fixedUiTpl('「{0} / {1}」包装清单数量之和（{2}）须等于单品数量（{3}）', colorName, sizeLabel, sum, pieces),
            'size',
            'supply',
          );
        }
      }

      const suggestedRaw = String(sku.suggestedPrice ?? '').trim();
      if (suggestedRaw) {
        const sp = Number(suggestedRaw);
        if (!Number.isFinite(sp) || sp <= 0) {
          return fail(fixedUiTpl('「{0} / {1}」建议零售价须为大于 0 的数字', colorName, sizeLabel), 'spec', 'supply');
        }
      }

      if (quantityCap.show) {
        const qtyRaw = String(sku.quantity ?? '').trim();
        if (quantityCap.required && !qtyRaw) {
          return fail(fixedUiTpl('请填写「{0} / {1}」包装件数', colorName, sizeLabel), 'size', 'supply');
        }
        if (qtyRaw) {
          const qty = Number(qtyRaw);
          if (!Number.isInteger(qty) || qty <= 0) {
            return fail(fixedUiTpl('「{0} / {1}」包装件数须为正整数', colorName, sizeLabel), 'size', 'supply');
          }
          const unit = Number(sku.quantityUnit || 1);
          if (![1, 2, 3].includes(unit)) {
            return fail(fixedUiTpl('「{0} / {1}」请选择包装单位（件/双/包）', colorName, sizeLabel), 'size', 'supply');
          }
        }
      }

      if (packageCap.show) {
        const pkgType = sku.packageType;
        const hasPkg = pkgType !== '' && pkgType != null && String(pkgType) !== '0';
        if (packageCap.required && !hasPkg) {
          return fail(fixedUiTpl('请选择「{0} / {1}」包装类型', colorName, sizeLabel), 'size', 'supply');
        }
        if (hasPkg) {
          const pt = Number(pkgType);
          if (![1, 2, 3, 4].includes(pt)) {
            return fail(fixedUiTpl('「{0} / {1}」包装类型无效', colorName, sizeLabel), 'size', 'supply');
          }
        }
      }

      if (sizeAttr?.values?.length && !sku.sizeValueId) {
        return fail(fixedUiTpl('「{0}」SKU 缺少{1}，请重新勾选{2}', colorName, skuSpecLabel, skuSpecLabel), 'spec', 'skcList');
      }
    }

    // 同 SKC 下件数单位须一致；若任一行填了件数，则该 SKC 全部 SKU 都须填写（仅规范允许时）
    if (quantityCap.show) {
      const qtyFilled = skuRows.filter((sku) => String(sku.quantity ?? '').trim());
      if (qtyFilled.length) {
        if (qtyFilled.length !== skuRows.length) {
          return fail(
            fixedUiTpl('「{0}」已填写部分 SKU 包装件数，同规格下全部 SKU 都须填写件数', colorName),
            'size',
            'supply',
          );
        }
        const units = new Set(
          skuRows.map((sku) => String(sku.quantityUnit || '1')),
        );
        if (units.size > 1) {
          return fail(
            fixedUiTpl('「{0}」同规格下包装单位须保持一致（件/双/套）', colorName),
            'size',
            'supply',
          );
        }
      }
    }

    // partialEdit：同 SKC 下任一 SKU 有 SKU 图时，其余 SKU 也须有图（平台会整体校验）
    if (isEdit) {
      const withImg = skuRows.filter((sku) => imageUrlOf(sku?.skuImage));
      if (withImg.length && withImg.length !== skuRows.length) {
        return fail(
          fixedUiTpl('「{0}」已为部分 SKU 上传 SKU 图，部分编辑要求同规格下全部 SKU 都提交 SKU 图', colorName),
          'spec',
          'skcList',
        );
      }
    }
  }

  // 素材图：可手传 1:1；未传时提交前会从主图自动裁成 1:1（须先有主图）
  if (!imageUrlOf(form.materialImage)) {
    const hasMain = (Array.isArray(form.skcList) ? form.skcList : [])
      .some((skc) => imageUrlOf(skc?.mainImage));
    if (!hasMain) {
      return fail(
        '请上传素材图（1:1，边长≥800px），或先上传主图以便自动裁成素材图',
        'spec',
        'materialImgUrl',
      );
    }
  }

  // 主图视频可选；有选则必须已转平台并取得 vid（Temu 要求 productCarouseVideoReqList[].vid 必填）
  {
    const videos = Array.isArray(form.temuVideos) ? form.temuVideos : [];
    for (const v of videos) {
      const vid = String(v?.vid || '').trim();
      const videoUrl = String(v?.videoUrl || '').trim();
      if (!vid && !videoUrl) {
        return fail('主图视频未完成 Temu 上传，请重新上传', 'spec', 'temuVideos');
      }
      if (!vid) {
        return fail('主图视频未取得平台 vid，请重新上传', 'spec', 'temuVideos');
      }
    }
  }

  const skuUniqueLocal = validateSupplierSkusLocal(form);
  if (!skuUniqueLocal.ok) return skuUniqueLocal;

  const productAttrsPool = Array.isArray(attrTpl?.productAttrs) && attrTpl.productAttrs.length
    ? attrTpl.productAttrs
    : [
      ...(attrTpl?.requiredProduct || []),
      ...(attrTpl?.optionalProduct || []),
      ...(attrTpl?.mainAttrs || []),
      ...(attrTpl?.otherAttrs || []),
    ];
  const { singles: requiredSingles, groups: requiredGroups } = collectEffectivelyRequiredAttrs(
    productAttrsPool,
    form.productAttributes || {},
  );
  const missingParentIssues = collectMissingParentAttrIssues(
    form.productAttributes || {},
    productAttrsPool,
  );
  if (missingParentIssues.length) {
    return fail(missingParentIssues[0].message, 'attrs', 'productAttributes');
  }
  for (const group of requiredGroups) {
    return fail(fixedUiTpl('请至少填写一项：{0}', group.label), 'attrs', 'productAttributes');
  }
  for (const attr of requiredSingles) {
    const val = form.productAttributes?.[attr.attributeId];
    if (attr.mode === 4 || attr.isComposition) {
      const rows = Array.isArray(val) ? val : [];
      if (!rows.length) return fail(fixedUiTpl('请填写必填属性：{0}', attr.name), 'attrs', 'productAttributes');
      for (const row of rows) {
        if (!row?.valueId) return fail(fixedUiTpl('{0}：请选择成分项', attr.name), 'attrs', 'productAttributes');
        if (!isPositiveNumber(row.percent)) return fail(fixedUiTpl('{0}：比例须为大于 0 的数字', attr.name), 'attrs', 'productAttributes');
      }
      const total = rows.reduce((s, r) => s + (Number(r.percent) || 0), 0);
      if (Math.round(total) !== 100) return fail(fixedUiTpl('{0} 比例合计须为 100%', attr.name), 'attrs', 'productAttributes');
    } else if (attr.mode === 1) {
      if (!Array.isArray(val) || !val.length) return fail(fixedUiTpl('请选择必填属性：{0}', attr.name), 'attrs', 'productAttributes');
    } else if (!isEditorAttrFilled(attr, form.productAttributes || {})) {
      return fail(fixedUiTpl('请填写必填属性：{0}', attr.name), 'attrs', 'productAttributes');
    }
  }

  // 官方尺码表必填由 sizecharts.class.get + 面板校验负责；不再用 attribute_type=2 空列表判定「无需填写」
  if (!sizeChartRequired && sizeMeasureAttrs.length) {
    const sizeCheck = validateSizeChartForm({
      sizeChart: form.sizeChart,
      measureAttrs: sizeMeasureAttrs,
      sizeAttr,
      selectedSizeIds: form.selectedSizeIds || [],
      skcList: form.skcList || [],
    });
    if (!sizeCheck.ok) {
      return fail(sizeCheck.message, sizeCheck.section || 'size', sizeCheck.field || 'sizeChart');
    }
  }

  const goodsModels = Array.isArray(form.goodsModels) ? form.goodsModels : [];
  // 部分叶子类目模特必填（平台公告清单）；未勾选则拦截
  if (modelRequired && !goodsModels.length) {
    return fail('当前类目要求填写模特信息，请选择模特', 'spec', 'goodsModels');
  }
  if (goodsModels.length) {
    const sizeOptions = buildTemuModelTryOnSizeOptions({
      sizeAttr,
      attrTpl,
      selectedSizeIds: form.selectedSizeIds,
      skcList: form.skcList,
    });
    for (const model of goodsModels) {
      const name = String(model?.modelName || fixedUi("模特")).trim() || fixedUi("模特");
      const sizeName = String(model?.sizeSpecName || '').trim();
      if (!sizeName || isIllegalTemuTryOnSizeName(sizeName)) {
        return fail(
          fixedUiTpl('模特「{0}」试穿尺码无效（不可为均码），请从商品已选尺码中选择', name),
          'spec',
          'goodsModels',
        );
      }
      if (!(Number(model?.sizeSpecId) > 0)) {
        return fail(
          fixedUiTpl('模特「{0}」缺少试穿尺码规格 ID，请重新选择试穿尺码', name),
          'spec',
          'goodsModels',
        );
      }
      if (sizeOptions.length) {
        const resolved = resolveGoodsModelTryOnSize(sizeName, sizeOptions);
        const matched = sizeOptions.some((opt) => (
          Number(opt.specId) === Number(model.sizeSpecId)
          || normalizeTemuTryOnSizeName(opt.name) === normalizeTemuTryOnSizeName(sizeName)
        ));
        if (!matched && !resolved) {
          return fail(
            fixedUiTpl('模特「{0}」试穿尺码「{1}」不在当前商品已选尺码中', name, sizeName),
            'spec',
            'goodsModels',
          );
        }
      }
    }
  }

  // 车型库：全托该类目 / 半托该类目且站点命中 + 适用车型=见详情 → 兼容车型必填
  if (isTemuVehicleLibraryDataRequired({
    categoryId: form.categoryId,
    shopType: shopType || form.shopType,
    selectedSites: form.selectedSites,
    attrTpl,
    productAttributes: form.productAttributes,
  }) && !hasTemuVehicleLibraryData(form)) {
    return fail(
      '当前类目「适用车型」为见详情，请填写兼容车型数据',
      'attrs',
      'goodsVehiclePropertyRelation',
    );
  }

  return { ok: true };
}
